/**
 * Customer hub card panel composition (ADR-0012 / ADR-0028).
 * Pure resolve — pack/settings order + permission filter; never tenant-id branching.
 */

export type CustomerCardPanelKey =
  | "contacts"
  | "subscriptions"
  | "payments"
  | "debts"
  | "mail_history"
  | "projects"
  | "finance"
  | "ledger"
  | "income_expenses"
  | "aliases";

export type CardPanelSetting = {
  key: string;
  enabled?: boolean;
  order?: number;
};

export type CustomerCardPanelDef = {
  key: CustomerCardPanelKey;
  permissionKey: string;
  defaultOrder: number;
  /** Static Hebrew label; projects may be overridden with entity labels. */
  labelHe: string;
};

export const CUSTOMER_CARD_PANEL_DEFS: readonly CustomerCardPanelDef[] = [
  {
    key: "contacts",
    permissionKey: "contacts.view",
    defaultOrder: 10,
    labelHe: "אנשי קשר",
  },
  {
    key: "subscriptions",
    permissionKey: "subscriptions.view",
    defaultOrder: 15,
    labelHe: "מנויים",
  },
  {
    key: "payments",
    permissionKey: "balances.view",
    defaultOrder: 16,
    labelHe: "תשלומים",
  },
  {
    key: "debts",
    permissionKey: "balances.view",
    defaultOrder: 17,
    labelHe: "חובות",
  },
  {
    key: "mail_history",
    permissionKey: "mail_log.view",
    defaultOrder: 18,
    labelHe: "היסטוריית דיוור",
  },
  {
    key: "projects",
    permissionKey: "projects.view",
    defaultOrder: 20,
    labelHe: "פרויקטים",
  },
  {
    key: "income_expenses",
    permissionKey: "balances.view",
    defaultOrder: 30,
    labelHe: "הכנסות והוצאות",
  },
  {
    key: "finance",
    permissionKey: "balances.view",
    defaultOrder: 35,
    labelHe: "מנויים ותשלומים",
  },
  {
    key: "ledger",
    permissionKey: "balances.view",
    defaultOrder: 40,
    labelHe: "עובר ושב (AR)",
  },
  {
    key: "aliases",
    permissionKey: "customers.view",
    defaultOrder: 50,
    labelHe: "כינויים",
  },
];

export const DEFAULT_CUSTOMER_CARD_PANELS: readonly CardPanelSetting[] = [
  { key: "contacts", enabled: true, order: 10 },
  { key: "subscriptions", enabled: true, order: 15 },
  { key: "payments", enabled: false, order: 16 },
  { key: "debts", enabled: false, order: 17 },
  { key: "mail_history", enabled: false, order: 18 },
  { key: "projects", enabled: true, order: 20 },
  { key: "income_expenses", enabled: true, order: 30 },
  { key: "finance", enabled: false, order: 35 },
  { key: "ledger", enabled: false, order: 40 },
  { key: "aliases", enabled: true, order: 50 },
];

const PANEL_KEY_SET = new Set<string>(CUSTOMER_CARD_PANEL_DEFS.map((d) => d.key));

function isPanelKey(key: string): key is CustomerCardPanelKey {
  return PANEL_KEY_SET.has(key);
}

export type ResolvedCustomerCardPanel = {
  key: CustomerCardPanelKey;
  order: number;
  permissionKey: string;
  labelHe: string;
};

/**
 * @param settings - from `activeTenant.cardPanels.customers` (or undefined → defaults)
 * @param grantedPermissions - effective permission keys
 * @param enabledFeatures - from `activeTenant.enabledFeatures` (module entitlements)
 */
export function resolveCustomerCardPanels(
  settings: readonly CardPanelSetting[] | null | undefined,
  grantedPermissions: Iterable<string>,
  enabledFeatures?: Iterable<string> | null,
): ResolvedCustomerCardPanel[] {
  const granted =
    grantedPermissions instanceof Set
      ? grantedPermissions
      : new Set(grantedPermissions);
  const featureGateActive = enabledFeatures != null;
  const features = featureGateActive
    ? enabledFeatures instanceof Set
      ? enabledFeatures
      : new Set(enabledFeatures)
    : null;

  const byKey = new Map(CUSTOMER_CARD_PANEL_DEFS.map((d) => [d.key, d]));
  const source =
    settings && settings.length > 0 ? settings : DEFAULT_CUSTOMER_CARD_PANELS;

  const resolved: ResolvedCustomerCardPanel[] = [];
  const seen = new Set<CustomerCardPanelKey>();

  for (const entry of source) {
    if (!isPanelKey(entry.key)) {
      continue;
    }
    if (entry.enabled === false) {
      seen.add(entry.key);
      continue;
    }
    const def = byKey.get(entry.key);
    if (!def || seen.has(entry.key)) {
      continue;
    }
    seen.add(entry.key);
    if (!granted.has(def.permissionKey)) {
      continue;
    }
    if (features) {
      if (entry.key === "income_expenses" && !features.has("finance.cash_ledger")) {
        continue;
      }
      if (
        (entry.key === "finance" ||
          entry.key === "ledger" ||
          entry.key === "subscriptions" ||
          entry.key === "payments" ||
          entry.key === "debts") &&
        !features.has("finance.ar")
      ) {
        continue;
      }
    }
    resolved.push({
      key: entry.key,
      order: entry.order ?? def.defaultOrder,
      permissionKey: def.permissionKey,
      labelHe: def.labelHe,
    });
  }

  resolved.sort((a, b) => a.order - b.order || a.key.localeCompare(b.key));
  return resolved;
}
